<template>
  <button
    class="popover__trigger"
    tabindex="-1"
    :style="{'anchor-name': `--${$props.targetId}`}"
    :popovertarget="$props.targetId"
  >
    <slot name="trigger" />
  </button>

  <div
    :id="$props.targetId"
    ref="menuRef"
    class="popover__container"
    popover="auto"
    :style="{'position-anchor': `--${$props.targetId}`}"
  >
    <div class="menu">
      <slot name="close">
        <div class="menu__close">
          <button :popovertarget="$props.targetId">
            <the-icon
              art="solid"
              name="xmark"
            />
          </button>
        </div>
      </slot>

      <slot />
    </div>
  </div>
</template>

<script setup>
  import { onMounted, useTemplateRef } from 'vue'
  import { v4 as uuidv4 } from 'uuid'

  // INIT
  const props = defineProps({
    targetId: {
      type: String,
      default: () => uuidv4()
    },

    preventAutoclose: {
      type: Boolean,
      default: false
    }
  })

  // DATA
  const menuRef = useTemplateRef('menuRef')

  // METHODS
  function hasActionableParent(domEl) {
    if (domEl && ['BUTTON', 'A'].includes(domEl.nodeName)) {
      return true
    } else if (domEl.parentElement) {
      return hasActionableParent(domEl.parentElement)
    } else {
      return false
    }
  }

  // LIFECYCLE HOOKS
  onMounted(() => {
    if (!props.preventAutoclose) {
      // @note: attach close action to all buttons and links -> can be disabled via prop
      menuRef.value.addEventListener('click', event => {
        if (hasActionableParent(event.target)) {
          window.setTimeout(() => {
            menuRef.value.hidePopover()
          }, 250)
        }
      })
    }
  })
</script>

<style scoped>
  .menu {
    background-color: var(--surface);
    box-shadow: var(--elevation-level-3);
    padding: var(--fixed-spacing-fix-04) var(--dotcom-responsive-gutter-responsive) var(--fixed-spacing-fix-08) var(--dotcom-responsive-gutter-responsive);
    border-radius: var(--fixed-border-radius-fix-04) var(--fixed-border-radius-fix-04) var(--fixed-border-radius-fix-00) var(--fixed-border-radius-fix-00);

    .menu__close {
      text-align: right;
      margin: 0 0 var(--fixed-spacing-fix-04) 0;

      button {
        color: var(--colors-primary-500);
        font-size: 24px;
        border: none;
        outline: none;
        background: none;
      }
    }
  }

  .popover__trigger {
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    height: 26px;
  }

  .popover__container {
    position: fixed;
    inset: unset;
    bottom: 0;
    left: 0;
    margin: 0;
    border: none;
    padding: 0;
    background: transparent;
    overflow: visible;
    width: 100%;

    opacity: 0;
    transform: scale(0.95) translateY(4px);
    transition:
      opacity 0.15s,
      transform 0.15s,
      display 0.15s allow-discrete;

    &:popover-open {
      opacity: 1;
      transform: scale(1) translateY(0);
    }

    &::backdrop {
      background: var(--scrim);
    }
  }

  @starting-style {
    .popover__container:popover-open {
      opacity: 0;
      transform: scale(0.95) translateY(4px);
    }
  }
</style>

<style scoped>
  @media (--v-medium) {
    .menu {
      background-color: var(--surface);
      border-radius: var(--fixed-spacing-fix-02);
      box-shadow: var(--elevation-level-3);
      overflow: hidden;
      padding: initial;

      .menu__close {
        display: none;
      }
    }

    .popover__container {
      position: fixed;
      inset: unset;
      top: calc(anchor(bottom) - 2px);
      left: anchor(left);
      translate: -50% 0;
      margin: 0;
      border: none;
      padding: 12px;
      background: transparent;
      overflow: visible;
      width: initial;

      &::backdrop {
        background: transparent;
      }
    }
  }
</style>